import { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  TextField, 
  InputAdornment, 
  IconButton, 
  Typography, 
  Button, 
  Chip, 
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  QrCodeScanner as ScannerIcon
} from '@mui/icons-material';
import BarcodeScanner from './BarcodeScanner';
import equipmentService from '../services/equipment.service';
import { inventoryApi } from '../services/api.service';
import FeatureGate from './FeatureGate';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

/**
 * Unified Equipment Selector Component
 * 
 * This component provides a consistent interface for selecting equipment
 * across all contexts: inventory management, job planning, etc.
 * 
 * @param {Object} props Component properties
 * @param {Function} props.onSelect Callback when equipment is selected
 * @param {boolean} props.multiple Allow multiple selection
 * @param {string} props.mode Mode of operation: 'all', 'available', 'planning'
 * @param {string} props.jobId Optional job ID for context
 * @param {Array} props.initialSelections Initially selected equipment
 * @param {boolean} props.showAvailability Show availability information
 * @param {boolean} props.hideActions Hide action buttons
 * @param {Function} props.onClose Callback when dialog is closed
 */
export default function EquipmentSelector({
  onSelect,
  multiple = false,
  mode = 'all',
  jobId = null,
  initialSelections = [],
  showAvailability = true,
  hideActions = false,
  onClose
}) {
  // Get feature flags
  const { isFeatureEnabled } = useFeatureFlags();
  const categoriesEnabled = isFeatureEnabled('EQUIPMENT_CATEGORIES');
  
  // State for equipment data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [availableOnly, setAvailableOnly] = useState(mode === 'available');
  const [categories, setCategories] = useState([]);
  
  // State for barcode scanning
  const [showScanner, setShowScanner] = useState(false);
  
  // State for detailed view
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // State for view mode
  const [viewMode, setViewMode] = useState(0); // 0: grid, 1: list

  // Set initial selections if provided
  useEffect(() => {
    if (initialSelections && initialSelections.length > 0) {
      setSelectedEquipment(initialSelections);
    }
  }, [initialSelections]);

  // Load equipment based on current filters
  const loadEquipment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query params
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoriesEnabled && categoryFilter) params.append('category', categoryFilter);
      if (availableOnly) params.append('available', 'true');
      
      // Add pagination
      params.append('limit', rowsPerPage.toString());
      params.append('offset', (page * rowsPerPage).toString());
      
      // If in planning mode for a specific job, include job context
      if (mode === 'planning' && jobId) {
        params.append('jobId', jobId);
      }
      
      // Use the service instead of direct fetch
      const data = await equipmentService.getEquipment({
        search: searchQuery,
        category: categoriesEnabled ? categoryFilter : undefined,
        status: availableOnly ? 'available' : undefined, // Simplified: use status filter directly
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        jobId: mode === 'planning' ? jobId : undefined
      });
      
      // Check if using the mock data structure or real API data structure
      if (data.equipment) {
        // Regular API response format
        setEquipment(data.equipment);
        setTotalCount(data.pagination?.totalCount || data.equipment.length || 0);
      } else if (Array.isArray(data)) {
        // Handle if API directly returns array of equipment
        setEquipment(data);
        setTotalCount(data.length);
      } else {
        // Handle any other format, check if data itself is the equipment array
        console.log('Equipment data format:', data);
        const equipmentData = data.data?.equipment || data.data || [];
        setEquipment(equipmentData);
        // Try to get total count from pagination, or from actual data length
        const paginationTotal = data.pagination?.totalCount || data.data?.pagination?.totalCount;
        const dataTotal = data.total || data.data?.total;
        const arrayLength = Array.isArray(equipmentData) ? equipmentData.length : 0;
        setTotalCount(paginationTotal || dataTotal || arrayLength || 0);
      }
      
      // Extract categories for filter dropdown
      const equipmentArray = data.equipment || 
                           (Array.isArray(data) ? data : data.data?.equipment || data.data || []);
      
      if (categoriesEnabled && equipmentArray && equipmentArray.length > 0) {
        // Only extract categories if feature is enabled
        const uniqueCategories = [...new Set(
          equipmentArray
            .map(item => item.category)
            .filter(Boolean)
        )].sort();
        
        setCategories(uniqueCategories);
      }
    } catch (err) {
      console.error('Error loading equipment:', err);
      setError(err.message || 'Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, availableOnly, page, rowsPerPage, mode, jobId]);

  // Load equipment on initial render and when filters change
  useEffect(() => {
    loadEquipment();
  }, [loadEquipment]);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle search submission
  const handleSearch = () => {
    setPage(0); // Reset to first page
    loadEquipment();
  };

  // Handle category filter change
  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
    setPage(0); // Reset to first page
  };

  // Handle availability filter change
  const handleAvailableOnlyChange = (event) => {
    setAvailableOnly(event.target.checked);
    setPage(0); // Reset to first page
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle equipment selection
  const handleSelect = (item) => {
    let newSelection;
    
    if (multiple) {
      // Check if already selected
      const isSelected = selectedEquipment.some(selected => selected.id === item.id);
      
      if (isSelected) {
        // Remove from selection
        newSelection = selectedEquipment.filter(selected => selected.id !== item.id);
      } else {
        // Add to selection
        newSelection = [...selectedEquipment, item];
      }
      setSelectedEquipment(newSelection);
      
      // Auto-call onSelect for multiple selection
      if (onSelect && typeof onSelect === 'function') {
        onSelect(newSelection);
      }
    } else {
      // Single selection - replace current selection
      newSelection = [item];
      setSelectedEquipment(newSelection);
      
      // If not multiple, auto-submit
      if (onSelect && typeof onSelect === 'function') {
        onSelect([item]);
      }
    }
  };

  // Handle clear all selections
  const handleClearSelections = () => {
    setSelectedEquipment([]);
    // Also notify parent of empty selection
    if (onSelect && typeof onSelect === 'function') {
      onSelect([]);
    }
  };

  // Handle confirm selection(s)
  const handleConfirmSelection = () => {
    if (onSelect && typeof onSelect === 'function') {
      onSelect(selectedEquipment);
    }
  };

  // Handle barcode scan
  const handleBarcodeScan = async (barcode) => {
    try {
      setLoading(true);
      setError(null);
      setShowScanner(false);
      
      // Search for equipment by id using the service
      const data = await equipmentService.getEquipment({
        search: barcode
      });
      
      // Determine the equipment array based on different possible response formats
      const equipmentArray = data.equipment || 
                           (Array.isArray(data) ? data : data.data?.equipment || data.data || []);
      
      if (equipmentArray && equipmentArray.length > 0) {
        // Find exact match
        const exactMatch = equipmentArray.find(item => 
          item.id === barcode || 
          item.serial_number === barcode
        );
        
        if (exactMatch) {
          // Select the item
          handleSelect(exactMatch);
        } else {
          // Select first result as best match
          handleSelect(equipmentArray[0]);
        }
        
        // Update equipment list with search results
        setEquipment(equipmentArray);
        setTotalCount(data.pagination?.totalCount || equipmentArray.length || 0);
      } else {
        throw new Error(`No equipment found with barcode ${barcode}`);
      }
    } catch (err) {
      console.error('Error handling barcode scan:', err);
      setError(err.message || 'Failed to process barcode');
    } finally {
      setLoading(false);
    }
  };

  // Handle view equipment details
  const handleViewDetails = async (item) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the service for fetching item details
      const data = await equipmentService.getEquipmentById(item.id, {
        includeInventory: true
      });
      
      // Handle different response formats
      const detailData = data.data || data;
      setSelectedDetail(detailData);
      setShowDetail(true);
    } catch (err) {
      console.error('Error loading equipment details:', err);
      setError(err.message || 'Failed to load equipment details');
    } finally {
      setLoading(false);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
  };

  // Render item availability chip (V2 compatible)
  const renderAvailabilityChip = (item) => {
    // V2 architecture: equipment items have a status field instead of availability object
    const status = item.status || 'unknown';
    
    switch (status.toLowerCase()) {
      case 'available':
        return (
          <Chip
            label="Available"
            color="success"
            size="small"
            variant="outlined"
          />
        );
      case 'assigned':
        return (
          <Chip
            label="Assigned"
            color="warning"
            size="small"
            variant="outlined"
          />
        );
      case 'maintenance':
        return (
          <Chip
            label="Maintenance"
            color="info"
            size="small"
            variant="outlined"
          />
        );
      case 'archived':
        return (
          <Chip
            label="Archived"
            color="default"
            size="small"
            variant="outlined"
          />
        );
      default:
        return (
          <Chip
            label="Not Available"
            color="error"
            size="small"
            variant="outlined"
          />
        );
    }
  };

  // Determine if an item is selected
  const isSelected = (item) => {
    return selectedEquipment.some(selected => selected.id === item.id);
  };

  // Grid view rendering
  const renderGridView = () => {
    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {equipment.map(item => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                height: 280, // Fixed height for consistent card sizing
                minHeight: 280,
                maxHeight: 280,
                border: isSelected(item) ? 2 : 0,
                borderColor: 'primary.main',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 3
                }
              }}
              onClick={() => handleSelect(item)}
            >
              <Box display="flex" flexDirection="column" height="100%">
                <Typography 
                  variant="subtitle1" 
                  fontWeight="medium" 
                  gutterBottom
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '48px'
                  }}
                >
                  {item.description || 'No Description'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Manufacturer:</strong> {item.manufacturer || 'N/A'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Model:</strong> {item.model || 'N/A'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Serial #:</strong> {item.serial_number || 'N/A'}
                </Typography>
                
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  ID: {item.id}
                </Typography>
                
                <FeatureGate feature="EQUIPMENT_CATEGORIES">
                  {item.category && (
                    <Chip 
                      label={item.category} 
                      size="small" 
                      sx={{ alignSelf: 'flex-start', mt: 1, mb: 1 }}
                    />
                  )}
                </FeatureGate>
                
                <Box flexGrow={1} />
                
                <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
                  {showAvailability && renderAvailabilityChip(item)}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  // List view rendering
  const renderListView = () => {
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Serial #</TableCell>
              <FeatureGate feature="EQUIPMENT_CATEGORIES">
                <TableCell>Category</TableCell>
              </FeatureGate>
              {showAvailability && <TableCell align="center">Availability</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {equipment.map(item => (
              <TableRow 
                key={item.id}
                hover
                selected={isSelected(item)}
                onClick={() => handleViewDetails(item)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {item.description}
                  </Typography>
                </TableCell>
                <TableCell>{item.manufacturer || 'N/A'}</TableCell>
                <TableCell>{item.model || 'N/A'}</TableCell>
                <TableCell>{item.serial_number || 'N/A'}</TableCell>
                <FeatureGate feature="EQUIPMENT_CATEGORIES">
                  <TableCell>
                    {item.category && (
                      <Chip label={item.category} size="small" />
                    )}
                  </TableCell>
                </FeatureGate>
                {showAvailability && (
                  <TableCell align="center">
                    {renderAvailabilityChip(item)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Pagination rendering
  const renderPagination = () => {
    return (
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    );
  };

  // Equipment detail dialog rendering
  const renderDetailDialog = () => {
    if (!selectedDetail) return null;
    
    const { 
      description, 
      id, 
      manufacturer, 
      model, 
      category, 
      subcategory,
      availability,
      inventoryItems 
    } = selectedDetail;
    
    return (
      <Dialog
        open={showDetail}
        onClose={() => setShowDetail(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Equipment Details
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <Typography variant="h6" gutterBottom>
              {description}
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>ID:</strong> {id}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  <strong>Manufacturer:</strong> {manufacturer || 'N/A'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  <strong>Model:</strong> {model || 'N/A'}
                </Typography>
              </Grid>
              
              <FeatureGate feature="EQUIPMENT_CATEGORIES">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Category:</strong> {category || 'N/A'}
                  </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  <strong>Subcategory:</strong> {subcategory || 'N/A'}
                </Typography>
                </Grid>
              </FeatureGate>
            </Grid>
            
            {availability && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Availability
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>In Stock:</strong>
                    </Typography>
                    <Typography variant="h6" color={availability.inStock > 0 ? 'success.main' : 'text.primary'}>
                      {availability.inStock}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reserved:</strong>
                    </Typography>
                    <Typography variant="h6" color={availability.assignedButAvailable > 0 ? 'warning.main' : 'text.primary'}>
                      {availability.assignedButAvailable || 0}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Assigned:</strong>
                    </Typography>
                    <Typography variant="h6">
                      {availability.assigned}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>On Order:</strong>
                    </Typography>
                    <Typography variant="h6" color={availability.ordered > 0 ? 'primary.main' : 'text.primary'}>
                      {availability.ordered}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Total Items:</strong>
                    </Typography>
                    <Typography variant="h6">
                      {availability.total}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
            
            {inventoryItems && inventoryItems.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Inventory Items
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Serial #</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Job</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryItems.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.serial_number || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.status} 
                              size="small"
                              color={
                                item.status === 'available' ? 'success' :
                                item.status === 'assigned' || item.status === 'maintenance' ? 'warning' :
                                item.status === 'archived' ? 'secondary' :
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            {item.warehouse ? `${item.warehouse.name}` : ''}
                            {item.rack ? ` / ${item.rack.name}` : ''}
                            {item.bay ? ` / ${item.bay.name}` : ''}
                          </TableCell>
                          <TableCell>
                            {item.job ? item.job.name : ''}
                            {item.site ? ` / ${item.site.name}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowDetail(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              setShowDetail(false);
              handleSelect(selectedDetail);
            }}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render the barcode scanner dialog
  const renderScannerDialog = () => {
    return (
      <Dialog
        open={showScanner}
        onClose={() => setShowScanner(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Scan Equipment Barcode
        </DialogTitle>
        
        <DialogContent>
          <BarcodeScanner 
            onScanSuccess={handleBarcodeScan}
            onScanError={(err) => setError(err)}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowScanner(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      {/* Search and filters */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder="Search equipment..."
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        
        <FeatureGate feature="EQUIPMENT_CATEGORIES">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={handleCategoryChange}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </FeatureGate>
        
        <Grid item xs={6} sm={2}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={availableOnly}
                  onChange={handleAvailableOnlyChange}
                  size="small"
                />
              }
              label="Available Only"
            />
          </FormGroup>
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Tooltip title="Scan Barcode">
              <IconButton onClick={() => setShowScanner(true)}>
                <ScannerIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      
      {/* View mode tabs and selection counter */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Tabs value={viewMode} onChange={handleViewModeChange}>
          <Tab label="Grid View" />
          <Tab label="List View" />
        </Tabs>
        
        {multiple && (
          <Badge badgeContent={selectedEquipment.length} color="primary">
            <Typography variant="body2" color="text.secondary" sx={{ pr: 2 }}>
              Selected
            </Typography>
          </Badge>
        )}
      </Box>
      
      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error.includes('Failed to load equipment:') 
            ? 'Unable to load equipment data. Please try again or contact support if this issue persists.' 
            : error}
        </Alert>
      )}
      
      {/* Loading indicator */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Equipment listing */}
      {!loading && equipment.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No equipment found matching your criteria.
          </Typography>
        </Paper>
      ) : (
        <>
          {viewMode === 0 ? renderGridView() : renderListView()}
          {renderPagination()}
        </>
      )}
      
      {/* Action buttons */}
      {/* Clear button for multiple selection */}
      {multiple && selectedEquipment.length > 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography variant="body2" color="text.secondary">
            {selectedEquipment.length} item{selectedEquipment.length !== 1 ? 's' : ''} selected
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            onClick={handleClearSelections}
          >
            Clear Selection
          </Button>
        </Box>
      )}
      
      {/* Detail dialog */}
      {renderDetailDialog()}
      
      {/* Scanner dialog */}
      {renderScannerDialog()}
    </Box>
  );
}